import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Icon,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  MarkerType,
  SelectionMode,
  useReactFlow,
  type Connection,
  type Edge,
  type Node,
  type NodeChange,
  type OnSelectionChangeParams,
  applyNodeChanges,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useTranslation } from "react-i18next";
import {
  AppDialog,
  AppDialogActions,
  AppDialogCancelButton,
  AppDialogContent,
  AppDialogPrimaryButton,
  AppDialogTitle,
} from "../../../components/dialog";
import { AppAutocomplete, AppButton, AppTextField } from "../../../components/form";
import { AppFormField, AppFormGrid, PageSectionCard } from "../../../components/layout";
import { PageHeader, PageListContainer } from "../../../components/Page";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import { GRID_FULL, GRID_HALF } from "../../../lib/ui/formLayout";
import { schemaAnalyticsService } from "../../../services/schemaAnalyticsService";
import { widgetService, type MappingFieldDescriptor } from "../../../services/widgetService";
import type {
  SchemaGraph,
  SchemaGraphEdge,
  SchemaGraphNode,
  SchemaRelationType,
} from "../../../types/schemaAnalytics";
import { getCurrentUserRoles, isWorkspaceAdmin } from "../../../utils/authorization";
import { autoLayoutSchemaNodes } from "../utils/autoLayout";
import {
  buildSchemaColorLegend,
  colorForSchemaGroup,
  colorForSchemaNode,
  schemaSurface,
  schemaNodeColorKey,
} from "../utils/schemaNodeColors";

type EdgeFormState = {
  id?: string;
  fromIndex: string;
  toIndex: string;
  relationType: SchemaRelationType;
  description: string;
  fromField: string;
  toField: string;
};

const RELATION_TYPES: SchemaRelationType[] = ["JOIN", "SEMANTIC", "BOTH"];

const emptyEdgeForm = (): EdgeFormState => ({
  fromIndex: "",
  toIndex: "",
  relationType: "BOTH",
  description: "",
  fromField: "",
  toField: "",
});

function nodeLabel(n: SchemaGraphNode): string {
  if (n.nodeKind === "ADAPTOR" || n.objectName) {
    const objectName = n.objectName || n.indexName;
    const adaptor = n.adaptorName || n.adaptorType;
    return adaptor ? `${objectName}\n(${adaptor})` : objectName;
  }
  return n.indexName;
}

function relationGroup(n: SchemaGraphNode): string {
  if (n.adaptorId) return `ADAPTOR:${n.adaptorId}`;
  if (n.indexName?.startsWith("adaptor:")) {
    const parts = n.indexName.split(":");
    if (parts.length >= 2) return `ADAPTOR:${parts[1]}`;
  }
  return "OPENSEARCH";
}

function sameRelationGroup(a?: SchemaGraphNode | null, b?: SchemaGraphNode | null): boolean {
  if (!a || !b) return false;
  return relationGroup(a) === relationGroup(b);
}

function graphToFlow(graph: SchemaGraph | null, dark: boolean): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = (graph?.nodes || []).map((n, i) => {
    const colors = colorForSchemaNode(n);
    const surface = schemaSurface(colors, dark);
    const colorKey = schemaNodeColorKey(n);
    const isManual = n.source === "MANUAL";
    return {
      id: n.indexName,
      position: {
        x: n.positionX ?? 80 + (i % 4) * 260,
        y: n.positionY ?? 80 + Math.floor(i / 4) * 160,
      },
      data: {
        label: nodeLabel(n),
        purpose: n.purpose || "",
        source: n.source || "AI",
        nodeKind: n.nodeKind || "OPENSEARCH",
        colorKey,
        adaptorId: n.adaptorId,
        adaptorName: n.adaptorName,
      },
      style: {
        border: `${isManual ? 2 : 1}px solid ${colors.border}`,
        borderRadius: 8,
        padding: 8,
        background: surface.background,
        color: surface.color,
        minWidth: 160,
        fontSize: 12,
        whiteSpace: "pre-line" as const,
      },
    };
  });
  const edges: Edge[] = (graph?.edges || []).map((e) => ({
    id: e.id || `${e.fromIndex}-${e.toIndex}`,
    source: e.fromIndex,
    target: e.toIndex,
    label: e.relationType || "SEMANTIC",
    animated: e.source !== "MANUAL",
    style: { stroke: e.locked || e.source === "MANUAL" ? "#F6BD16" : "#5B8FF9", strokeWidth: 2 },
    interactionWidth: 28,
    markerEnd: { type: MarkerType.ArrowClosed },
    data: e,
  }));
  return { nodes, edges };
}

function fieldLabel(field: MappingFieldDescriptor): string {
  return field.type ? `${field.path} (${field.type})` : field.path;
}

function isAdaptorNodeKey(indexName: string): boolean {
  return /^adaptor:[0-9a-f-]{36}:.+/i.test(indexName);
}

function SchemaAnalyticsPageInner() {
  const { t } = useTranslation();
  const { confirmDelete, isCancelled } = useDeleteConfirm();
  const { fitView } = useReactFlow();
  const dark = useTheme().palette.mode === "dark";
  const darkRef = useRef(dark);
  darkRef.current = dark;
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);
  const [graph, setGraph] = useState<SchemaGraph | null>(null);
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [admin, setAdmin] = useState(false);
  const [selectedNode, setSelectedNode] = useState<SchemaGraphNode | null>(null);
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const [selectedEdgeMeta, setSelectedEdgeMeta] = useState<SchemaGraphEdge | null>(null);
  const [edgeDialogOpen, setEdgeDialogOpen] = useState(false);
  const [edgeForm, setEdgeForm] = useState<EdgeFormState>(emptyEdgeForm);
  const [fromFields, setFromFields] = useState<MappingFieldDescriptor[]>([]);
  const [toFields, setToFields] = useState<MappingFieldDescriptor[]>([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [sampleDialogOpen, setSampleDialogOpen] = useState(false);
  const [kvkkNotice, setKvkkNotice] = useState("");
  const [consentChecked, setConsentChecked] = useState(false);
  const pendingPublishRef = useRef(false);

  const discardPending = useCallback(() => {
    if (!pendingPublishRef.current) return;
    pendingPublishRef.current = false;
    void schemaAnalyticsService.discard().catch(() => undefined);
  }, []);

  const fitCanvas = useCallback(() => {
    requestAnimationFrame(() => {
      void fitView({ padding: 0.18, duration: 280 }).catch(() => undefined);
    });
  }, [fitView]);

  const persistNodePositions = useCallback(
    async (laidOut: Node[]) => {
      if (!admin || laidOut.length === 0) return;
      try {
        const positions = laidOut.map((n) => ({
          indexName: n.id,
          positionX: n.position.x,
          positionY: n.position.y,
        }));
        const g = await schemaAnalyticsService.updatePositions(positions);
        setGraph(g);
      } catch {
        /* ignore position save errors */
      }
    },
    [admin]
  );

  const applyGraph = useCallback(
    (g: SchemaGraph, options?: { layout?: boolean; persistLayout?: boolean }) => {
      setGraph(g);
      pendingPublishRef.current = Boolean(g.pendingPublish);
      const flow = graphToFlow(g, darkRef.current);
      const nextNodes = options?.layout
        ? autoLayoutSchemaNodes(flow.nodes, flow.edges)
        : flow.nodes;
      setNodes(nextNodes);
      setEdges(flow.edges);
      fitCanvas();
      if (options?.persistLayout && options.layout) {
        void persistNodePositions(nextNodes);
      }
    },
    [fitCanvas, persistNodePositions]
  );

  // Theme switch: recolor in place. Do not rebuild nodes from selection — that loops setNodes (React #185).
  useEffect(() => {
    setNodes((nds) => {
      let changed = false;
      const next = nds.map((n) => {
        const base = colorForSchemaGroup(
          String((n.data as { colorKey?: string } | undefined)?.colorKey ?? "OPENSEARCH")
        );
        const surface = schemaSurface(base, dark);
        if (n.style?.background === surface.background && n.style?.color === surface.color) {
          return n;
        }
        changed = true;
        return {
          ...n,
          style: {
            ...n.style,
            background: surface.background,
            color: surface.color,
          },
        };
      });
      return changed ? next : nds;
    });
  }, [dark]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const g = await schemaAnalyticsService.getGraph();
      const needsLayout =
        (g.nodes || []).length > 0 &&
        (g.nodes || []).every((n) => n.positionX == null && n.positionY == null);
      applyGraph(g, { layout: needsLayout, persistLayout: needsLayout && admin });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t("schemaAnalytics.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [applyGraph, admin, t]);

  useEffect(() => {
    void getCurrentUserRoles().then((roles) => setAdmin(isWorkspaceAdmin(roles)));
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onHide = () => discardPending();
    window.addEventListener("pagehide", onHide);
    return () => {
      window.removeEventListener("pagehide", onHide);
      discardPending();
    };
  }, [discardPending]);

  const loadMappingFields = useCallback(async (fromIndex: string, toIndex: string) => {
    setFieldsLoading(true);
    try {
      const [fromRes, toRes] = await Promise.all([
        fromIndex
          ? widgetService.getMappingHints(fromIndex).catch(() => null)
          : Promise.resolve(null),
        toIndex
          ? widgetService.getMappingHints(toIndex).catch(() => null)
          : Promise.resolve(null),
      ]);
      setFromFields(fromRes?.fields || []);
      setToFields(toRes?.fields || []);
    } finally {
      setFieldsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!edgeDialogOpen) return;
    void loadMappingFields(edgeForm.fromIndex, edgeForm.toIndex);
  }, [edgeDialogOpen, edgeForm.fromIndex, edgeForm.toIndex, loadMappingFields]);

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
  }, []);

  const persistPositions = useCallback(async () => {
    await persistNodePositions(nodes);
  }, [persistNodePositions, nodes]);

  const handleAutoArrange = useCallback(async () => {
    const laidOut = autoLayoutSchemaNodes(nodes, edges);
    setNodes(laidOut);
    fitCanvas();
    await persistNodePositions(laidOut);
  }, [nodes, edges, fitCanvas, persistNodePositions]);

  const runAnalyze = async () => {
    setBusy(true);
    setError(null);
    try {
      const g = await schemaAnalyticsService.analyze();
      applyGraph(g, { layout: true, persistLayout: true });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t("schemaAnalytics.analyzeFailed"));
    } finally {
      setBusy(false);
    }
  };

  const openSampleDialog = async () => {
    setConsentChecked(false);
    try {
      const n = await schemaAnalyticsService.kvkkNotice();
      setKvkkNotice(n.notice || t("schemaAnalytics.kvkkDefault"));
    } catch {
      setKvkkNotice(t("schemaAnalytics.kvkkDefault"));
    }
    setSampleDialogOpen(true);
  };

  const runEnrich = async () => {
    if (!consentChecked) return;
    setBusy(true);
    setError(null);
    setSampleDialogOpen(false);
    try {
      const g = await schemaAnalyticsService.enrichSamples({ consentGiven: true, sampleSize: 3 });
      applyGraph(g, { layout: true, persistLayout: true });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t("schemaAnalytics.enrichFailed"));
    } finally {
      setBusy(false);
    }
  };

  const runPublish = async () => {
    setBusy(true);
    setError(null);
    try {
      const g = await schemaAnalyticsService.publish();
      applyGraph(g);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t("schemaAnalytics.publishFailed"));
    } finally {
      setBusy(false);
    }
  };

  const openAddEdgeDialog = (prefill?: Partial<EdgeFormState>) => {
    setSelectedEdgeMeta(null);
    setEdgeForm({ ...emptyEdgeForm(), ...prefill });
    setEdgeDialogOpen(true);
  };

  const openEditEdgeDialog = (edge: SchemaGraphEdge) => {
    setSelectedNode(null);
    setSelectedEdgeMeta(edge);
    setEdgeForm({
      id: edge.id,
      fromIndex: edge.fromIndex || "",
      toIndex: edge.toIndex || "",
      relationType: (edge.relationType as SchemaRelationType) || "BOTH",
      description: edge.description || "",
      fromField: edge.fieldMappings?.[0]?.fromField || "",
      toField: edge.fieldMappings?.[0]?.toField || "",
    });
    setEdgeDialogOpen(true);
  };

  const resolveGraphEdge = useCallback(
    (flowEdge: Edge): SchemaGraphEdge | null => {
      const byId = (graph?.edges || []).find((e) => e.id && e.id === flowEdge.id);
      if (byId) return byId;
      const data = flowEdge.data as SchemaGraphEdge | undefined;
      if (data?.fromIndex && data?.toIndex) return data;
      const byEndpoints = (graph?.edges || []).find(
        (e) => e.fromIndex === flowEdge.source && e.toIndex === flowEdge.target
      );
      return byEndpoints || null;
    },
    [graph]
  );

  const closeEdgeDialog = () => {
    setEdgeDialogOpen(false);
    setSelectedEdgeMeta(null);
    setEdgeForm(emptyEdgeForm());
    setFromFields([]);
    setToFields([]);
  };

  const warnCrossSourceLink = useCallback(() => {
    setError(null);
    setWarning(t("schemaAnalytics.crossSourceLinkBlocked"));
  }, [t]);

  const isValidConnection = useCallback(
    (connection: Connection | { source: string | null; target: string | null }) => {
      if (!connection.source || !connection.target) return false;
      if (connection.source === connection.target) return false;
      const from = (graph?.nodes || []).find((n) => n.indexName === connection.source);
      const to = (graph?.nodes || []).find((n) => n.indexName === connection.target);
      return sameRelationGroup(from, to);
    },
    [graph]
  );

  const onConnect = (connection: Connection) => {
    if (!admin || !connection.source || !connection.target) return;
    const from = (graph?.nodes || []).find((n) => n.indexName === connection.source);
    const to = (graph?.nodes || []).find((n) => n.indexName === connection.target);
    if (!sameRelationGroup(from, to)) {
      warnCrossSourceLink();
      return;
    }
    setWarning(null);
    openAddEdgeDialog({
      fromIndex: connection.source,
      toIndex: connection.target,
      relationType: "BOTH",
      description: t("schemaAnalytics.manualEdgeDefault"),
    });
  };

  const saveEdgeForm = async () => {
    if (!admin || !edgeForm.fromIndex || !edgeForm.toIndex) return;
    const from = (graph?.nodes || []).find((n) => n.indexName === edgeForm.fromIndex);
    const to = (graph?.nodes || []).find((n) => n.indexName === edgeForm.toIndex);
    if (!sameRelationGroup(from, to)) {
      warnCrossSourceLink();
      return;
    }
    setBusy(true);
    setError(null);
    setWarning(null);
    try {
      const fieldMappings =
        edgeForm.fromField || edgeForm.toField
          ? [{ fromField: edgeForm.fromField || undefined, toField: edgeForm.toField || undefined }]
          : [];
      const g = await schemaAnalyticsService.upsertEdge({
        id: edgeForm.id || selectedEdgeMeta?.id,
        fromIndex: edgeForm.fromIndex,
        toIndex: edgeForm.toIndex,
        relationType: edgeForm.relationType,
        description: edgeForm.description,
        fieldMappings,
      });
      applyGraph(g, { layout: true, persistLayout: true });
      closeEdgeDialog();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : t("schemaAnalytics.saveFailed");
      if (/same source group|same adaptor|OpenSearch indices together/i.test(message)) {
        setWarning(t("schemaAnalytics.crossSourceLinkBlocked"));
      } else {
        setError(message);
      }
    } finally {
      setBusy(false);
    }
  };

  const deleteSelectedEdge = async () => {
    const edgeId = edgeForm.id || selectedEdgeMeta?.id;
    if (!admin || !edgeId) return;
    try {
      await confirmDelete({
        itemName:
          selectedEdgeMeta?.fromIndex && selectedEdgeMeta?.toIndex
            ? `${selectedEdgeMeta.fromIndex} → ${selectedEdgeMeta.toIndex}`
            : edgeId,
      });
    } catch (e: unknown) {
      if (isCancelled(e)) return;
      throw e;
    }
    setBusy(true);
    try {
      const g = await schemaAnalyticsService.deleteEdge(edgeId);
      applyGraph(g);
      closeEdgeDialog();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t("schemaAnalytics.saveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const saveSelectedNode = async () => {
    if (!admin || !selectedNode?.indexName) return;
    setBusy(true);
    try {
      const g = await schemaAnalyticsService.upsertNode({
        indexName: selectedNode.indexName,
        purpose: selectedNode.purpose,
        dataSummary: selectedNode.dataSummary,
        keyFields: selectedNode.keyFields,
      });
      applyGraph(g);
      setSelectedNode(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t("schemaAnalytics.saveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const onSelectionChange = useCallback(({ nodes: selected }: OnSelectionChangeParams) => {
    const ids = selected.map((n) => n.id);
    setSelectedNodeIds((prev) => {
      if (prev.length === ids.length && prev.every((id, i) => id === ids[i])) {
        return prev;
      }
      return ids;
    });
  }, []);

  const indexOptions = useMemo(
    () => (graph?.nodes || []).map((n) => n.indexName).filter(Boolean) as string[],
    [graph]
  );

  const colorLegend = useMemo(() => buildSchemaColorLegend(graph?.nodes), [graph?.nodes]);

  const nodeByIndex = useMemo(() => {
    const map = new Map<string, SchemaGraphNode>();
    (graph?.nodes || []).forEach((n) => {
      if (n.indexName) map.set(n.indexName, n);
    });
    return map;
  }, [graph]);

  const optionLabel = (indexName: string) => {
    const n = nodeByIndex.get(indexName);
    if (!n) return indexName;
    if (n.objectName) {
      return n.adaptorName ? `${n.objectName} (${n.adaptorName})` : n.objectName;
    }
    return n.indexName;
  };

  const deleteNodesByIds = async (ids: string[]) => {
    if (!admin || ids.length === 0) return;
    const unique = [...new Set(ids.filter(Boolean))];
    if (unique.length === 0) return;
    try {
      await confirmDelete({
        itemName: unique.length === 1 ? optionLabel(unique[0]) || unique[0] : undefined,
        titleKey:
          unique.length === 1 ? "schemaAnalytics.deleteNodeTitle" : "schemaAnalytics.deleteSelectedTitle",
        descriptionKey:
          unique.length === 1
            ? "schemaAnalytics.deleteNodeDescription"
            : "schemaAnalytics.deleteSelectedDescription",
        descriptionValues: unique.length === 1 ? undefined : { count: unique.length },
      });
    } catch (e: unknown) {
      if (isCancelled(e)) return;
      throw e;
    }
    setBusy(true);
    setError(null);
    try {
      let g: SchemaGraph | null = null;
      for (const id of unique) {
        g = await schemaAnalyticsService.deleteNode(id);
      }
      if (g) applyGraph(g);
      setSelectedNode(null);
      setSelectedNodeIds([]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t("schemaAnalytics.saveFailed"));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!admin) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || target?.isContentEditable) return;
      if (edgeDialogOpen || selectedNode || sampleDialogOpen) return;
      if (selectedNodeIds.length === 0 || busy) return;
      e.preventDefault();
      void deleteNodesByIds(selectedNodeIds);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [admin, busy, edgeDialogOpen, sampleDialogOpen, selectedNode, selectedNodeIds]);

  const toIndexOptions = useMemo(() => {
    const from = edgeForm.fromIndex ? nodeByIndex.get(edgeForm.fromIndex) : undefined;
    if (!from) return indexOptions;
    const group = relationGroup(from);
    return indexOptions.filter((id) => {
      if (id === from.indexName) return false;
      const node = nodeByIndex.get(id);
      return node ? relationGroup(node) === group : false;
    });
  }, [edgeForm.fromIndex, indexOptions, nodeByIndex]);

  const edgeFormCrossGroup = useMemo(() => {
    if (!edgeForm.fromIndex || !edgeForm.toIndex) return false;
    return !sameRelationGroup(
      nodeByIndex.get(edgeForm.fromIndex),
      nodeByIndex.get(edgeForm.toIndex)
    );
  }, [edgeForm.fromIndex, edgeForm.toIndex, nodeByIndex]);

  const fromFieldOptions = useMemo(() => fromFields.map((f) => f.path), [fromFields]);
  const toFieldOptions = useMemo(() => toFields.map((f) => f.path), [toFields]);
  const fieldHelper = (index: string) => {
    if (fieldsLoading) return t("schemaAnalytics.fieldsLoading");
    if (!index) return t("schemaAnalytics.selectIndexFirst");
    return t(
      isAdaptorNodeKey(index) ? "schemaAnalytics.fieldsFromAdaptor" : "schemaAnalytics.fieldsFromMapping"
    );
  };
  const fromFieldByPath = useMemo(() => {
    const map = new Map<string, MappingFieldDescriptor>();
    fromFields.forEach((f) => map.set(f.path, f));
    return map;
  }, [fromFields]);
  const toFieldByPath = useMemo(() => {
    const map = new Map<string, MappingFieldDescriptor>();
    toFields.forEach((f) => map.set(f.path, f));
    return map;
  }, [toFields]);

  const editingExisting = Boolean(edgeForm.id || selectedEdgeMeta?.id);

  const closeActionsMenu = () => setActionsAnchor(null);
  /** Close the menu first so a dialog opened by the action does not fight it for focus. */
  const runMenuAction = (action: () => void) => () => {
    closeActionsMenu();
    action();
  };

  const headerActions = (
    <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap alignItems="center">
      {admin && (
        <AppButton variant="contained" disabled={busy || loading} onClick={() => void runAnalyze()}>
          {busy ? t("schemaAnalytics.working") : t("schemaAnalytics.analyze")}
        </AppButton>
      )}
      {admin && selectedNodeIds.length > 0 && (
        <AppButton
          color="error"
          variant="outlined"
          disabled={busy || loading}
          onClick={() => void deleteNodesByIds(selectedNodeIds)}
        >
          {t("schemaAnalytics.deleteSelected")} ({selectedNodeIds.length})
        </AppButton>
      )}
      <AppButton
        variant="outlined"
        aria-haspopup="menu"
        aria-expanded={Boolean(actionsAnchor)}
        endIcon={<Icon fontSize="small">expand_more</Icon>}
        onClick={(e) => setActionsAnchor(e.currentTarget)}
      >
        {t("common.actions")}
      </AppButton>
      <Menu anchorEl={actionsAnchor} open={Boolean(actionsAnchor)} onClose={closeActionsMenu}>
        {admin && (
          <MenuItem disabled={busy || loading} onClick={runMenuAction(() => void openSampleDialog())}>
            <ListItemIcon><Icon fontSize="small">science</Icon></ListItemIcon>
            {t("schemaAnalytics.enrichSamples")}
          </MenuItem>
        )}
        {admin && (
          <MenuItem
            disabled={busy || loading || indexOptions.length < 2}
            onClick={runMenuAction(() => openAddEdgeDialog())}
          >
            <ListItemIcon><Icon fontSize="small">add_link</Icon></ListItemIcon>
            {t("schemaAnalytics.addEdge")}
          </MenuItem>
        )}
        {admin && (
          <MenuItem disabled={busy || loading} onClick={runMenuAction(() => void runPublish())}>
            <ListItemIcon><Icon fontSize="small">publish</Icon></ListItemIcon>
            {t("schemaAnalytics.publish")}
          </MenuItem>
        )}
        {admin && <Divider />}
        <MenuItem
          disabled={busy || loading || nodes.length === 0}
          onClick={runMenuAction(() => void handleAutoArrange())}
        >
          <ListItemIcon><Icon fontSize="small">auto_fix_high</Icon></ListItemIcon>
          {t("schemaAnalytics.autoArrange")}
        </MenuItem>
        <MenuItem disabled={busy || loading} onClick={runMenuAction(() => void load())}>
          <ListItemIcon><Icon fontSize="small">refresh</Icon></ListItemIcon>
          {t("schemaAnalytics.refresh")}
        </MenuItem>
      </Menu>
    </Stack>
  );

  return (
    <PageListContainer surface="plain">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: { xs: "auto", md: "calc(100vh - 140px)" },
          minHeight: { xs: 560, md: 480 },
          gap: 1.5,
        }}
      >
        <PageHeader
          title={t("schemaAnalytics.title")}
          subtitle={t("schemaAnalytics.subtitle")}
          actions={headerActions}
          mb={1}
        />

      <Stack direction="row" flexWrap="wrap" gap={2} sx={{ flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary">
          {t("schemaAnalytics.status")}: {graph?.status || "—"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t("schemaAnalytics.nodes")}: {graph?.nodes?.length ?? 0}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t("schemaAnalytics.edges")}: {graph?.edges?.length ?? 0}
        </Typography>
        {graph?.lastAnalyzedAt && (
          <Typography variant="caption" color="text.secondary">
            {t("schemaAnalytics.lastAnalyzed")}: {new Date(graph.lastAnalyzedAt).toLocaleString()}
          </Typography>
        )}
        {graph?.publishedAt && (
          <Typography variant="caption" color="text.secondary">
            {t("schemaAnalytics.publishedAt")}: {new Date(graph.publishedAt).toLocaleString()}
          </Typography>
        )}
      </Stack>

      {graph?.pendingPublish && (
        <Alert severity="info" sx={{ flexShrink: 0 }}>
          {t("schemaAnalytics.pendingPublish")}
        </Alert>
      )}

      {colorLegend.length > 0 && (
        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          gap={1.5}
          sx={{ flexShrink: 0 }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {t("schemaAnalytics.legend")}:
          </Typography>
          {colorLegend.map((entry) => (
            <Stack key={entry.colorKey} direction="row" alignItems="center" gap={0.75}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: 0.5,
                  border: `1px solid ${entry.border}`,
                  bgcolor: schemaSurface(entry, dark).background,
                  flexShrink: 0,
                }}
                aria-hidden
              />
              <Typography variant="caption" color="text.secondary">
                {entry.colorKey === "OPENSEARCH"
                  ? t("schemaAnalytics.nodeKindOpenSearch")
                  : entry.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {!admin && (
        <Alert severity="info" sx={{ flexShrink: 0 }}>
          {t("schemaAnalytics.viewOnly")}
        </Alert>
      )}
      {admin && (
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
          {t("schemaAnalytics.selectionHint")}
        </Typography>
      )}
      {error && (
        <Alert severity="error" sx={{ flexShrink: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {warning && (
        <Alert severity="warning" sx={{ flexShrink: 0 }} onClose={() => setWarning(null)}>
          {warning}
        </Alert>
      )}

      <PageSectionCard noPadding fillHeight>
        <Box
          sx={{
            flex: 1,
            minHeight: { xs: 420, md: 360 },
            height: { md: "100%" },
            overflow: "hidden",
            position: "relative",
            "& .react-flow": { width: "100%", height: "100%" },
            // Selection ring via CSS — avoid rewriting `nodes` on select (React #185 / RF setNodes loop).
            "& .react-flow__node.selected": {
              boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`,
            },
          }}
          onMouseLeave={() => {
            if (admin) void persistPositions();
          }}
        >
          <ReactFlow
            colorMode={dark ? "dark" : "light"}
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onConnect={onConnect}
            isValidConnection={isValidConnection}
            nodesDraggable={admin}
            nodesConnectable={admin}
            elementsSelectable
            selectionOnDrag={admin}
            selectionMode={SelectionMode.Partial}
            multiSelectionKeyCode="Shift"
            panOnDrag={admin ? [1, 2] : true}
            deleteKeyCode={null}
            onSelectionChange={onSelectionChange}
            fitView
            onNodeDoubleClick={(_, node) => {
              closeEdgeDialog();
              const raw = (graph?.nodes || []).find((n) => n.indexName === node.id) || null;
              setSelectedNode(raw ? { ...raw } : null);
            }}
            onEdgeClick={(_, edge) => {
              const raw = resolveGraphEdge(edge);
              if (raw) {
                openEditEdgeDialog(raw);
              }
            }}
            onEdgeDoubleClick={(_, edge) => {
              const raw = resolveGraphEdge(edge);
              if (raw) {
                openEditEdgeDialog(raw);
              }
            }}
          >
            <Background />
            <Controls />
            <MiniMap />
          </ReactFlow>
        </Box>
      </PageSectionCard>

      <AppDialog open={edgeDialogOpen} onClose={closeEdgeDialog} maxWidth="sm" fullWidth>
        <AppDialogTitle>
          {editingExisting ? t("schemaAnalytics.edgeDetail") : t("schemaAnalytics.addEdgeTitle")}
        </AppDialogTitle>
        <AppDialogContent>
          {edgeFormCrossGroup && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              {t("schemaAnalytics.crossSourceLinkBlocked")}
            </Alert>
          )}
          <AppFormGrid sx={{ pt: 1 }}>
            <AppFormField size={GRID_HALF}>
              <AppAutocomplete<string>
                label={t("schemaAnalytics.fromIndex")}
                options={indexOptions}
                value={edgeForm.fromIndex || null}
                disabled={!admin}
                getOptionLabel={optionLabel}
                onChange={(_, value) =>
                  setEdgeForm((f) => {
                    const nextFrom = value || "";
                    const fromNode = nextFrom ? nodeByIndex.get(nextFrom) : undefined;
                    const toNode = f.toIndex ? nodeByIndex.get(f.toIndex) : undefined;
                    const clearTo =
                      fromNode && toNode && relationGroup(fromNode) !== relationGroup(toNode);
                    return {
                      ...f,
                      fromIndex: nextFrom,
                      toIndex: clearTo ? "" : f.toIndex,
                      fromField: "",
                      toField: clearTo ? "" : f.toField,
                    };
                  })
                }
              />
            </AppFormField>
            <AppFormField size={GRID_HALF}>
              <AppAutocomplete<string>
                label={t("schemaAnalytics.toIndex")}
                options={toIndexOptions}
                value={edgeForm.toIndex || null}
                disabled={!admin}
                getOptionLabel={optionLabel}
                onChange={(_, value) =>
                  setEdgeForm((f) => ({
                    ...f,
                    toIndex: value || "",
                    toField: "",
                  }))
                }
              />
            </AppFormField>
            <AppFormField size={GRID_HALF}>
              <AppAutocomplete<SchemaRelationType>
                label={t("schemaAnalytics.relationType")}
                options={RELATION_TYPES}
                value={edgeForm.relationType}
                disabled={!admin}
                disableClearable
                onChange={(_, value) =>
                  setEdgeForm((f) => ({ ...f, relationType: value || "BOTH" }))
                }
              />
            </AppFormField>
            <AppFormField size={GRID_FULL}>
              <AppTextField
                label={t("schemaAnalytics.description")}
                multiline
                minRows={3}
                value={edgeForm.description}
                disabled={!admin}
                onChange={(e) => setEdgeForm((f) => ({ ...f, description: e.target.value }))}
              />
            </AppFormField>
            <AppFormField size={GRID_HALF}>
              <AppAutocomplete<string>
                label={t("schemaAnalytics.fromField")}
                helperText={fieldHelper(edgeForm.fromIndex)}
                options={fromFieldOptions}
                value={edgeForm.fromField || null}
                disabled={!admin || !edgeForm.fromIndex}
                loading={fieldsLoading}
                getOptionLabel={(path) => {
                  const field = fromFieldByPath.get(path);
                  return field ? fieldLabel(field) : path;
                }}
                onChange={(_, value) => setEdgeForm((f) => ({ ...f, fromField: value || "" }))}
              />
            </AppFormField>
            <AppFormField size={GRID_HALF}>
              <AppAutocomplete<string>
                label={t("schemaAnalytics.toField")}
                helperText={fieldHelper(edgeForm.toIndex)}
                options={toFieldOptions}
                value={edgeForm.toField || null}
                disabled={!admin || !edgeForm.toIndex}
                loading={fieldsLoading}
                getOptionLabel={(path) => {
                  const field = toFieldByPath.get(path);
                  return field ? fieldLabel(field) : path;
                }}
                onChange={(_, value) => setEdgeForm((f) => ({ ...f, toField: value || "" }))}
              />
            </AppFormField>
            {selectedEdgeMeta && (
              <AppFormField size={GRID_FULL}>
                <Typography variant="caption" color="text.secondary">
                  {t("schemaAnalytics.source")}: {selectedEdgeMeta.source || "—"}
                  {selectedEdgeMeta.locked ? ` (${t("schemaAnalytics.locked")})` : ""}
                </Typography>
              </AppFormField>
            )}
          </AppFormGrid>
        </AppDialogContent>
        <AppDialogActions>
          {admin && editingExisting && (
            <AppButton color="error" disabled={busy} onClick={() => void deleteSelectedEdge()} sx={{ mr: "auto" }}>
              {t("schemaAnalytics.delete")}
            </AppButton>
          )}
          <AppDialogCancelButton onClick={closeEdgeDialog}>{t("schemaAnalytics.cancel")}</AppDialogCancelButton>
          {admin && (
            <AppDialogPrimaryButton
              disabled={busy || !edgeForm.fromIndex || !edgeForm.toIndex || edgeFormCrossGroup}
              onClick={() => void saveEdgeForm()}
            >
              {t("schemaAnalytics.save")}
            </AppDialogPrimaryButton>
          )}
        </AppDialogActions>
      </AppDialog>

      <AppDialog
        open={Boolean(selectedNode)}
        onClose={() => setSelectedNode(null)}
        maxWidth="sm"
        fullWidth
      >
        <AppDialogTitle>
          {selectedNode
            ? selectedNode.objectName || selectedNode.indexName
            : t("schemaAnalytics.nodeDetail")}
        </AppDialogTitle>
        <AppDialogContent>
          {selectedNode && (
            <AppFormGrid>
              <AppFormField size={GRID_FULL}>
                <Typography variant="body2" color="text.secondary">
                  {selectedNode.nodeKind === "ADAPTOR" || selectedNode.objectName
                    ? t("schemaAnalytics.nodeKindAdaptor")
                    : t("schemaAnalytics.nodeKindOpenSearch")}
                  {selectedNode.adaptorName || selectedNode.adaptorType
                    ? ` · ${t("schemaAnalytics.adaptorMeta")}: ${[
                        selectedNode.adaptorName,
                        selectedNode.adaptorType,
                      ]
                        .filter(Boolean)
                        .join(" / ")}`
                    : ""}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  {selectedNode.indexName}
                </Typography>
              </AppFormField>
              <AppFormField size={GRID_FULL}>
                <AppTextField
                  label={t("schemaAnalytics.purpose")}
                  size="small"
                  multiline
                  minRows={3}
                  value={selectedNode.purpose || ""}
                  disabled={!admin}
                  onChange={(e) => setSelectedNode({ ...selectedNode, purpose: e.target.value })}
                />
              </AppFormField>
              <AppFormField size={GRID_FULL}>
                <AppTextField
                  label={t("schemaAnalytics.dataSummary")}
                  size="small"
                  multiline
                  minRows={4}
                  value={selectedNode.dataSummary || ""}
                  disabled={!admin}
                  onChange={(e) => setSelectedNode({ ...selectedNode, dataSummary: e.target.value })}
                />
              </AppFormField>
              <AppFormField size={GRID_FULL}>
                <AppTextField
                  label={t("schemaAnalytics.keyFields")}
                  size="small"
                  helperText={t("schemaAnalytics.keyFieldsHint")}
                  value={(selectedNode.keyFields || []).join(", ")}
                  disabled={!admin}
                  onChange={(e) =>
                    setSelectedNode({
                      ...selectedNode,
                      keyFields: e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                />
              </AppFormField>
            </AppFormGrid>
          )}
        </AppDialogContent>
        <AppDialogActions>
          <AppDialogCancelButton onClick={() => setSelectedNode(null)}>
            {t("schemaAnalytics.cancel")}
          </AppDialogCancelButton>
          {admin && selectedNode?.indexName && (
            <AppButton
              color="error"
              variant="outlined"
              disabled={busy}
              onClick={() => {
                const id = selectedNode.indexName!;
                setSelectedNode(null);
                void deleteNodesByIds([id]);
              }}
            >
              {t("schemaAnalytics.delete")}
            </AppButton>
          )}
          {admin && (
            <AppDialogPrimaryButton disabled={busy} onClick={() => void saveSelectedNode()}>
              {t("schemaAnalytics.save")}
            </AppDialogPrimaryButton>
          )}
        </AppDialogActions>
      </AppDialog>

      <Dialog open={sampleDialogOpen} onClose={() => setSampleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("schemaAnalytics.enrichSamples")}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {kvkkNotice}
          </Alert>
          <FormControlLabel
            control={
              <Checkbox checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)} />
            }
            label={t("schemaAnalytics.consentLabel")}
          />
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {t("schemaAnalytics.enrichHint")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <AppButton onClick={() => setSampleDialogOpen(false)}>{t("schemaAnalytics.cancel")}</AppButton>
          <AppButton variant="contained" disabled={!consentChecked || busy} onClick={() => void runEnrich()}>
            {t("schemaAnalytics.confirmEnrich")}
          </AppButton>
        </DialogActions>
      </Dialog>
      </Box>
    </PageListContainer>
  );
}

export default function SchemaAnalyticsPage() {
  return (
    <ReactFlowProvider>
      <SchemaAnalyticsPageInner />
    </ReactFlowProvider>
  );
}
